/**
 * Media TanStack Mutation hooks.
 *
 * Handles media updates, deletion, moving, and bulk operations.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mediaKeys, folderKeys, exploreKeys } from "@/queries/keys";
import { MEDIA_ID_PATTERN } from "@/lib/constants/media";
import { trackFailure } from "@/lib/integrations/sentry-tracking";
import { trackMediaExport } from "@/lib/integrations/ga4";
import {
  updateMedia,
  deleteMedia,
  moveMedia,
  bulkDeleteMedia,
  bulkReanalyzeMedia,
  updateMediaRemark,
  updateMediaTagsAndRemark,
  exportMediaMultiple,
  reanalyzeMediaFull,
  reanalyzeInsights,
  retranscribeMedia,
  reuploadText,
  updateFavorite,
  setMediaThumbnailImage,
  requestMediaThumbnailFrame,
} from "./api";
import type { ThumbnailView } from "@speakai/shared";
import type { MediaDetail } from "./api";
import { getMediaStatus } from "@/features/media-insight/api";

const BULK_CHUNK_SIZE = 10;
const BULK_MAX_ITEMS = 500;
const THUMBNAIL_FRAME_POLL_MS = 3000;
const THUMBNAIL_FRAME_WAIT_MS = 60_000;
const THUMBNAIL_FRAME_BACKGROUND_WAIT_MS = 5 * 60_000;

async function runInChunks<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
): Promise<PromiseSettledResult<unknown>[]> {
  const results: PromiseSettledResult<unknown>[] = [];
  for (let i = 0; i < items.length; i += BULK_CHUNK_SIZE) {
    const chunk = items.slice(i, i + BULK_CHUNK_SIZE);
    const chunkResults = await Promise.allSettled(chunk.map(fn));
    results.push(...chunkResults);
  }
  return results;
}

export function validateAndCapIds(ids: string[]): string[] {
  if (ids.length === 0) return [];

  const patternValid = ids.filter((id) => MEDIA_ID_PATTERN.test(id));
  const dropped = ids.length - patternValid.length;

  if (dropped > 0) {
    trackFailure("media", "invalid_ids_filtered", {
      submitted: ids.length,
      dropped,
    });
  }

  // Sending an empty array is never valid — the server returns 404.
  if (patternValid.length === 0) {
    throw new Error("MEDIA_IDS_INVALID");
  }

  return patternValid.slice(0, BULK_MAX_ITEMS);
}

// -- Types -----------------------------------------------------------

interface UpdateMediaParams {
  mediaId: string;
  name?: string;
  description?: string;
  folderId?: string;
  tags?: string[];
  remark?: string;
  status?: string;
  manageBy?: string;
  createdAt?: string;
}

interface MoveMediaParams {
  mediaIds: string[];
  folderId: string;
}

interface UpdateFavoriteParams {
  mediaIds: string[];
  isFavorite: boolean;
}

type SetMediaThumbnailParams =
  { mediaId: string; image: File } | { mediaId: string; seekSec: number };

export type ThumbnailFailure =
  "invalidImage" | "busy" | "frame" | "processing" | "unknown";

export class ThumbnailUpdateError extends Error {
  constructor(
    public readonly failure: ThumbnailFailure,
    message: string,
  ) {
    super(message);
    this.name = "ThumbnailUpdateError";
  }
}

function thumbnailFailureFor(
  status: number | undefined,
  isFrame: boolean,
): ThumbnailFailure {
  if (status === 409 || status === 503) return "busy";
  if (status === 400) return isFrame ? "frame" : "invalidImage";
  if (status === 413) return "invalidImage";
  return "unknown";
}

async function waitForThumbnailFrame(
  mediaId: string,
  waitMs: number,
): Promise<ThumbnailView> {
  const deadline = Date.now() + waitMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) =>
      setTimeout(resolve, THUMBNAIL_FRAME_POLL_MS),
    );
    const res = await getMediaStatus(mediaId);
    const thumbnail = res.data?.thumbnail;
    if (!res.success || !thumbnail || thumbnail.pending) continue;
    if (thumbnail.error)
      throw new ThumbnailUpdateError("frame", thumbnail.error);
    return thumbnail;
  }
  throw new ThumbnailUpdateError("processing", "The frame is still being made");
}

// -- Mutation Hooks --------------------------------------------------

/** Update a single media's details. */
export function useUpdateMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateMediaParams) => {
      const res = await updateMedia(params);
      if (!res.success) throw new Error(res.error ?? "Failed to update media");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mediaKeys.detail(variables.mediaId),
      });
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
    },
  });
}

/** Delete a single media. */
export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await deleteMedia(mediaId);
      if (!res.success) throw new Error(res.error ?? "Failed to delete media");
      return res.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: exploreKeys.all });
    },
  });
}

/** Move media items to a folder. */
export function useMoveMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: MoveMediaParams) => {
      const res = await moveMedia(params);
      if (!res.success) throw new Error(res.error ?? "Failed to move media");
      return res.data!;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: folderKeys.all });
    },
  });
}

/** Bulk delete multiple media items. */
export function useBulkDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaIds: string[]) => {
      const validIds = validateAndCapIds(mediaIds);
      const res = await bulkDeleteMedia(validIds);
      if (!res.success)
        throw new Error(res.error ?? "Failed to bulk delete media");
      return res.data!;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: exploreKeys.all });
    },
  });
}

/**
 * Bulk re-analyze multiple media items.
 *
 * Calls server's bulk endpoint POST /media/reAnalyzeMultiple — does not fan
 * out per id. Server expects { mediaIds, isInsights, isSentiment,
 * isFillerWords, isEmbeddings }. sourceLanguage is intentionally NOT sent —
 * Joi reAnalyzeParamsSchema rejects unknown keys.
 */
export function useBulkReanalyzeMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { mediaIds: string[] }) => {
      const validIds = validateAndCapIds(params.mediaIds);
      // reAnalyzeMultiple 500s on a single id; the single-item path is useReanalyzeMedia.
      if (validIds.length < 2) {
        throw new Error("REANALYZE_MIN_TWO");
      }
      const res = await bulkReanalyzeMedia(validIds);
      if (!res.success)
        throw new Error(res.error ?? "Failed to re-analyze media");
      return {
        total: validIds.length,
        failed: 0,
        capped: params.mediaIds.length > BULK_MAX_ITEMS,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: exploreKeys.all });
    },
  });
}

/**
 * Bulk update tags/category on multiple media items.
 *
 * Server contract (Joi):
 *  - PUT /media/:mediaId — `mediaUpdateBody` REQUIRES `name`. Used for tag updates.
 *  - PUT /media/remark/:mediaId — `mediaRemarkBody` accepts `{ remark }` only.
 *
 * To stay compatible:
 *  - Remark-only updates → routed through /media/remark/:id (no name required).
 *  - Tag updates (with or without remark) → /media/:id with `name` echoed.
 *
 * Callers supplying tags MUST pass `{ mediaId, name }` pairs via `items`.
 * Remark-only callers may pass `mediaIds` (no names needed).
 */
export function useBulkUpdateMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      items: Array<{ mediaId: string; name: string }>;
      tags?: string[];
      remark?: string;
    }) => {
      const validItems = params.items
        .filter((it) => MEDIA_ID_PATTERN.test(it.mediaId))
        .slice(0, BULK_MAX_ITEMS);

      const isRemarkOnly =
        params.tags === undefined && params.remark !== undefined;

      const results = await runInChunks(validItems, async (item) => {
        if (isRemarkOnly) {
          // Fast path: dedicated remark endpoint — no `name` required.
          const res = await updateMediaRemark(item.mediaId, params.remark);
          if (!res.success) throw new Error(res.error ?? "Failed to update");
          return res.data;
        }

        // Tag (and optionally remark) update — server requires `name`.
        const res = await updateMediaTagsAndRemark(item.mediaId, {
          mediaId: item.mediaId,
          name: item.name,
          ...(params.tags !== undefined ? { tags: params.tags } : {}),
          ...(params.remark !== undefined ? { remark: params.remark } : {}),
        });
        if (!res.success) throw new Error(res.error ?? "Failed to update");
        return res.data;
      });

      const failures = results.filter((r) => r.status === "rejected");
      if (validItems.length > 0 && failures.length === validItems.length) {
        throw new Error("Failed to update all media");
      }
      return {
        total: validItems.length,
        failed: failures.length,
        capped: params.items.length > BULK_MAX_ITEMS,
      };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: exploreKeys.all });
    },
  });
}

/** Export media via postBlob, wrapped in mutation for loading/error state. */
export function useExportMedia() {
  return useMutation({
    mutationFn: async (params: {
      mediaType: string;
      fileType: string;
      mediaIds: string[];
      isSpeakerNames: boolean;
      isTimeStamps: boolean;
      isRedacted: boolean;
      redactedCategories: string[];
      isMerged: boolean;
      folderId: string;
      isInsightVisualized?: boolean;
      isSpeakerEmail?: boolean;
    }) => {
      const blob = await exportMediaMultiple(params.mediaType, {
        fileType: params.fileType,
        mediaIds: params.mediaIds,
        isSpeakerNames: params.isSpeakerNames,
        isTimeStamps: params.isTimeStamps,
        isInsightVisualized: params.isInsightVisualized ?? false,
        isSpeakerEmail: params.isSpeakerEmail ?? false,
        isRedacted: params.isRedacted,
        redactedCategories: params.redactedCategories,
        isMerged: params.isMerged,
        folderId: params.folderId,
      });
      return blob;
    },
    onSuccess: () => {
      trackMediaExport();
    },
  });
}

// -- Reanalyze / Retranscribe ----------------------------------------

interface ReanalyzeInsightsParams {
  mediaId: string;
  isInsights: boolean;
  isSentiment: boolean;
  isFillerWords: boolean;
  isEmbeddings?: boolean;
}

interface ReTranscribeParams {
  mediaId: string;
  targetLanguage: string;
}

/** Re-analyze a single media item (queues background job). */
/**
 * Full re-analyze (all signals) for a single media item.
 *
 * Server route: GET /v1/media/reanalyze/:mediaId — same endpoint as
 * useReanalyzeInsights, just with all flags = true. Mirrors Angular's
 * mediaService.reanalyzeInsights() pattern (media.service.ts:437-450).
 */
export function useReanalyzeMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await reanalyzeMediaFull(mediaId);
      if (!res.success) throw new Error(res.error ?? "Failed to re-analyze");
      return res.data!;
    },
    onSettled: (_data, _err, mediaId) => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.detail(mediaId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: mediaKeys.insights(mediaId) });
      queryClient.invalidateQueries({
        queryKey: mediaKeys.textInsight(mediaId),
      });
    },
  });
}

/** Re-analyze insights for a single media item with granular options. */
export function useReanalyzeInsights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ReanalyzeInsightsParams) => {
      const res = await reanalyzeInsights(params);
      if (!res.success)
        throw new Error(res.error ?? "Failed to re-analyze insights");
      return res.data!;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mediaKeys.detail(variables.mediaId),
      });
      queryClient.invalidateQueries({
        queryKey: mediaKeys.insights(variables.mediaId),
      });
      queryClient.invalidateQueries({
        queryKey: mediaKeys.textInsight(variables.mediaId),
      });
    },
  });
}

/** Re-transcribe a media item in a new language — creates new media. */
export function useReTranscribe() {
  return useMutation({
    mutationFn: async (params: ReTranscribeParams) => {
      const res = await retranscribeMedia(params);
      if (!res.success)
        throw new Error(res.error ?? "Failed to re-transcribe media");
      return res.data!;
    },
  });
}

/** Re-upload a text-type media file when it has been stuck in 'preparing' for >1 day. */
export function useReuploadText() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mediaId: string) => {
      const res = await reuploadText(mediaId);
      if (!res.success) throw new Error(res.error || "Failed to re-upload");
      return res.data;
    },
    onSuccess: (_data, mediaId) => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.detail(mediaId) });
      queryClient.invalidateQueries({ queryKey: mediaKeys.insights(mediaId) });
      queryClient.invalidateQueries({
        queryKey: mediaKeys.textInsight(mediaId),
      });
    },
  });
}

/** Toggle favorite status for one or more media items (with optimistic update). */
export function useUpdateFavorite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateFavoriteParams) => {
      const res = await updateFavorite(params);
      if (!res.success)
        throw new Error(res.error ?? "Failed to update favorite status");
      return res.data!;
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: mediaKeys.lists() });
      const previous = queryClient.getQueriesData({
        queryKey: mediaKeys.lists(),
      });

      queryClient.setQueriesData(
        { queryKey: mediaKeys.lists() },
        (
          old:
            | {
                mediaList: Array<{ mediaId: string; isFavorite?: boolean }>;
                [k: string]: unknown;
              }
            | undefined,
        ) => {
          if (!old?.mediaList) return old;
          return {
            ...old,
            mediaList: old.mediaList.map((m) =>
              params.mediaIds.includes(m.mediaId)
                ? { ...m, isFavorite: params.isFavorite }
                : m,
            ),
          };
        },
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        for (const [queryKey, data] of context.previous) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
    },
  });
}

/** Replace a video's thumbnail with an uploaded image, or with the frame at `seekSec` (waits up to a minute for the frame). */
export function useSetMediaThumbnail() {
  const queryClient = useQueryClient();

  const applyThumbnail = (mediaId: string, thumbnail: ThumbnailView) => {
    queryClient.setQueryData<MediaDetail>(
      mediaKeys.detail(mediaId),
      (old) => old && { ...old, thumbnail },
    );
    queryClient.invalidateQueries({ queryKey: mediaKeys.lists() });
  };

  return useMutation({
    mutationFn: async (
      params: SetMediaThumbnailParams,
    ): Promise<ThumbnailView> => {
      const isFrame = "seekSec" in params;
      const res = isFrame
        ? await requestMediaThumbnailFrame(params.mediaId, params.seekSec)
        : await setMediaThumbnailImage(params.mediaId, params.image);
      if (!res.success) {
        throw new ThumbnailUpdateError(
          thumbnailFailureFor(res.status, isFrame),
          res.error ?? "Failed to update the thumbnail",
        );
      }
      return isFrame
        ? waitForThumbnailFrame(params.mediaId, THUMBNAIL_FRAME_WAIT_MS)
        : res.data!.thumbnail;
    },
    onSuccess: (thumbnail, { mediaId }) => applyThumbnail(mediaId, thumbnail),
    onError: (err, { mediaId }) => {
      if (
        !(err instanceof ThumbnailUpdateError) ||
        err.failure !== "processing"
      )
        return;
      waitForThumbnailFrame(mediaId, THUMBNAIL_FRAME_BACKGROUND_WAIT_MS)
        .then((thumbnail) => applyThumbnail(mediaId, thumbnail))
        .catch(() =>
          queryClient.invalidateQueries({
            queryKey: mediaKeys.detail(mediaId),
          }),
        );
    },
  });
}
